import React, { useEffect, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Image, Upload, Modal, message, Popover } from 'antd';
import imageCompression from "browser-image-compression";
import watermark from "watermarkjs";
import api from '../api/axios';
import { useProfile } from '../context/ProfileContext';

const getBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

const ManagePhotos = () => {
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');
    const [fileList, setFileList] = useState([]);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [deleteFile, setDeleteFile] = useState(null);

    const { profileId } = useProfile();

    useEffect(() => {
        if (!profileId) {
            message.error('Profile ID is missing. Please log in again.');
            return;
        }

        // Fetch the list of profile pictures
        const fetchProfilePictures = async () => {
            try {
                const response = await api.get('/profile-pictures/list', {
                    params: { profile_id: profileId },
                });

                if (response.data.status === 'success') {
                    const pictures = response.data.data.map((pic) => ({
                        uid: pic.id,
                        name: pic.image_path.split('/').pop(),
                        status: 'done',
                        url: `${process.env.REACT_APP_IMAGE_URL}/${pic.image_path}`,
                    }));
                    setFileList(pictures);
                } else {
                    message.error('Failed to fetch profile pictures.');
                }
            } catch (error) {
                message.error('An error occurred while fetching profile pictures.');
                console.error(error);
            }
        };

        fetchProfilePictures();
    }, [profileId]);

    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
        setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
    };

    const handleChange = async ({ file, fileList: newFileList }) => {
        setFileList(newFileList);

        if (file.status === undefined) {
            // Step 1: Compress the image
            const options = {
                maxSizeMB: 0.1, // 100KB limit
                maxWidthOrHeight: 800, // Resize if needed
                useWebWorker: true,
                fileType: 'image/jpeg'
            };

            const compressedFile = await imageCompression(file, options);
            const base64Image = await imageCompression.getDataUrlFromFile(compressedFile);

            // Step 2: Apply watermark
            const watermarkedImage = await watermark([base64Image]).image(watermark.text.lowerRight('Oottan', '28px Arial', 'rgba(255,255,255,0.6)', 1));

            // Step 3: Convert to Blob
            const response = await fetch(watermarkedImage.src);
            const blob = await response.blob();
            const formData = new FormData();
            formData.append('profile_id', profileId);
            formData.append('image', new File([blob], file.name, { type: blob.type }));

            try {
                const response = await api.post('/profile-pictures/add', formData);

                if (response.status === 200) {
                    message.success('Image uploaded successfully!');
                    const updatedFileList = newFileList.map((f) => {
                        if (f.uid === file.uid) {
                            return { ...f, status: 'done', uid: response.data.data.id };
                        }
                        return f;
                    });
                    setFileList(updatedFileList);
                } else {
                    message.error('Failed to upload image.');
                    const updatedFileList = newFileList.map((f) => {
                        if (f.uid === file.uid) {
                            return { ...f, status: 'error' };
                        }
                        return f;
                    });
                    setFileList(updatedFileList);
                }
            } catch (error) {
                message.error('An error occurred while uploading the image.');
                const updatedFileList = newFileList.map((f) => {
                    if (f.uid === file.uid) {
                        return { ...f, status: 'error' };
                    }
                    return f;
                });
                setFileList(updatedFileList);
                console.error(error);
            }
        }
    };

    const handleDelete = (file) => {
        setDeleteFile(file);
        setIsDeleteModalVisible(true);
        return false;
    };

    const confirmDelete = async () => {
        if (!deleteFile) return;

        try {
            const response = await api.delete(
                `/profile-pictures/delete/${deleteFile.uid}`,
                { data: { profile_id: profileId } }
            );

            if (response.data.status === 'success') {
                message.success('Image deleted successfully.');
                setFileList(fileList.filter((file) => file.uid !== deleteFile.uid));
            } else {
                message.error('Failed to delete image.');
                console.error(response.data.message);
            }
        } catch (error) {
            message.error('An error occurred while deleting the image.');
            console.error(error);
        } finally {
            setIsDeleteModalVisible(false);
            setDeleteFile(null);
        }
    };

    const cancelDelete = () => {
        setIsDeleteModalVisible(false);
        setDeleteFile(null);
    };

    const popoverContent = (
        <div>
            <p style={{ fontSize: '0.75rem', margin: 0 }}>
                Acceptable file types: .png, .jpg, .jpeg.
            </p>
            <p style={{ fontSize: '0.75rem', margin: 0 }}>
                Maximum upload size: 2 MB.
            </p>
        </div>
    );

    const uploadButton = (
        <Popover content={popoverContent} title="File Upload Guidelines" trigger="hover">
            <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
            </div>
        </Popover>
    );

    return (
        <>
            <Upload
                listType="picture-card"
                accept=".png,.jpg,.jpeg"
                fileList={fileList}
                beforeUpload={() => false}
                onPreview={handlePreview}
                onChange={handleChange}
                onRemove={handleDelete}
            >
                {fileList.length >= 5 ? null : uploadButton}
            </Upload>
            <Image
                preview={{
                    visible: previewOpen,
                    src: previewImage,
                    title: previewTitle,
                    onVisibleChange: (visible) => setPreviewOpen(visible),
                }}
                src={previewImage}
                style={{ display: 'none' }}
            />
            <Modal
                title="Confirm Delete"
                open={isDeleteModalVisible}
                onOk={confirmDelete}
                onCancel={cancelDelete}
                okText="Delete"
                cancelText="Cancel"
            >
                <p>Are you sure you want to delete this image?</p>
            </Modal>
        </>
    );
};

export default ManagePhotos;
